import React, { useState, useEffect } from 'react';
import { jobsAPI, uploadAPI } from '../../services/api';
import BBCodeEditor from './BBCodeEditor';
import '../admin/AdminComponents.css';

const JobForm = ({ job, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    active: true,
    displayOrder: '',
    categoryId: '',
    location: 'Los Santos',
    employmentType: 'Full-time',
    openings: 1,
    backgroundImage: '',
    backgroundColor: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    jobsAPI.getCategories(true)
      .then(response => setCategories(response.success ? response.data : []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || '',
        description: job.description || '',
        active: job.active !== undefined ? job.active : true,
        displayOrder: job.display_order || '',
        categoryId: job.category_id || '',
        location: job.location || 'Los Santos',
        employmentType: job.employment_type || 'Full-time',
        openings: job.openings || 1,
        backgroundImage: job.background_image || '',
        backgroundColor: job.background_color || ''
      });
    }
  }, [job]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          backgroundImage: response.data.url || response.data.path
        }));
      } else {
        alert(response.message || 'Error uploading image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message || 'Error uploading image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        active: formData.active,
        displayOrder: formData.displayOrder ? parseInt(formData.displayOrder) : undefined,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        location: formData.location.trim() || 'Los Santos',
        employmentType: formData.employmentType.trim() || 'Full-time',
        openings: Math.max(1, parseInt(formData.openings) || 1),
        backgroundImage: formData.backgroundImage.trim() || null,
        backgroundColor: formData.backgroundColor.trim() || null
      };

      if (job) {
        await jobsAPI.update(job.id, submitData);
      } else {
        await jobsAPI.create(submitData);
      }

      onClose();
    } catch (error) {
      setError(error.message || 'Failed to save job position');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{job ? 'Edit Job Position' : 'Add Job Position'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>

        </div>
        <form onSubmit={handleSubmit} className="admin-form">
          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}
          <div className="admin-form-group">
            <label className="admin-form-label">
              Title <span className="admin-required">*</span>
            </label>
            <input
              type="text"
              name="title"
              className="admin-form-input"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Senior Developer"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Filter Category</label>
            <select
              name="categoryId"
              className="admin-form-select"
              value={formData.categoryId}
              onChange={handleChange}
            >
              <option value="">Uncategorised</option>
              {categories.filter(category => category.active || category.id === Number(formData.categoryId)).map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            <span className="admin-form-hint">Manage available filters from the Careers category list.</span>
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Location</label>
            <input
              type="text"
              name="location"
              className="admin-form-input"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Los Santos"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Employment Type</label>
            <input
              type="text"
              name="employmentType"
              className="admin-form-input"
              value={formData.employmentType}
              onChange={handleChange}
              placeholder="e.g., Full-time, Part-time, Contract"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Openings</label>
            <input
              type="number"
              min="1"
              name="openings"
              className="admin-form-input"
              value={formData.openings}
              onChange={handleChange}
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Short Description</label>
            <BBCodeEditor
              value={formData.description}
              onChange={(description) => {
                setFormData(previous => ({ ...previous, description }));
                setError('');
              }}
              placeholder="A concise summary shown in the careers list..."
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Display Order</label>
            <input
              type="number"
              name="displayOrder"
              className="admin-form-input"
              value={formData.displayOrder}
              onChange={handleChange}
              placeholder="Order in list (lower appears first)"
            />
            <span className="admin-form-hint">Leave empty to add at the end</span>
          </div>

          <div className="admin-form-group">
            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              Accepting Applications
            </label>
            <span className="admin-form-hint">Uncheck to stop accepting new applications</span>
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Background Image (Optional)</label>
            <input
              type="text"
              name="backgroundImage"
              className="admin-form-input"
              value={formData.backgroundImage}
              onChange={handleChange}
              placeholder="Image URL or upload"
            />
            <input
              type="file"
              accept="image/*"
              className="admin-form-input-file"
              onChange={(e) => handleImageUpload(e.target.files[0])}
            />
            {formData.backgroundImage && (
              <img
                src={formData.backgroundImage}
                alt="Background preview"
                className="admin-image-preview-small"
                style={{ marginTop: '0.5rem', maxWidth: '200px', maxHeight: '100px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span className="admin-form-hint">Upload an image to use as background instead of solid color</span>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Background Color (Optional)</label>
            <input
              type="color"
              name="backgroundColor"
              className="admin-form-input"
              value={formData.backgroundColor || '#1a1a1a'}
              onChange={handleChange}
              style={{ width: '100px', height: '40px' }}
            />
            <input
              type="text"
              name="backgroundColor"
              className="admin-form-input"
              value={formData.backgroundColor}
              onChange={handleChange}
              placeholder="#1a1a1a"
              style={{ marginTop: '0.5rem' }}
            />

            <span className="admin-form-hint">Used if no background image is set</span>

          </div>


          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="admin-button primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : job ? 'Update' : 'Create'}
            </button>

          </div>

        </form>

      </div>

    </div>

  );
};

export default JobForm;
